import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { fmtDate, fmtINR, today } from "@/lib/format";
import { Repeat, Play, Trash2, Plus, Calendar, CheckCircle2 } from "lucide-react";

const CADENCES = [
  { v: "weekly", l: "Weekly" }, { v: "monthly", l: "Monthly" },
  { v: "quarterly", l: "Quarterly" }, { v: "yearly", l: "Yearly" },
];
const DOC_TYPES = [
  { v: "sales_invoice", l: "Sales Invoice" },
  { v: "purchase_bill", l: "Purchase Bill" },
  { v: "journal", l: "Journal Voucher" },
];

export default function Recurring() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    name: "", doc_type: "sales_invoice", cadence: "monthly",
    start_date: today(), end_date: "",
    body: { branch_id: "", customer_id: "", vendor_id: "", lines: [{ description: "", qty: 1, rate: 0, gst_rate: 18 }], narration: "" },
  });

  const load = async () => setRows((await api.get("/recurring")).data);
  useEffect(() => {
    load();
    api.get("/parties?party_type=customer").then((r) => setCustomers(r.data));
    api.get("/parties?party_type=vendor").then((r) => setVendors(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/branches").then((r) => { setBranches(r.data); setForm((f) => ({ ...f, body: { ...f.body, branch_id: r.data[0]?.id } })); });
  }, []);

  const setLine = (i, patch) => setForm((f) => ({ ...f, body: { ...f.body, lines: f.body.lines.map((l, j) => j === i ? { ...l, ...patch } : l) } }));
  const addLine = () => setForm((f) => ({ ...f, body: { ...f.body, lines: [...f.body.lines, { description: "", qty: 1, rate: 0, gst_rate: 18 }] } }));

  const save = async () => {
    try {
      await api.post("/recurring", form);
      toast.success("Recurring created"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runDue = async () => {
    try {
      const r = await api.post("/recurring/run-due");
      toast.success(`Created ${r.data.count} document(s)`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (id, cur) => {
    try { await api.post(`/recurring/${id}/toggle`, { is_active: !cur }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this recurring definition?")) return;
    try { await api.delete(`/recurring/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="recurring-page">
      <PageHeader eyebrow="Automation" title="Recurring Documents"
        description="Schedule invoices, bills and journals to auto-post on a cadence. Perfect for retainers, subscriptions, rent, EMIs."
        actions={
          <>
            <Button variant="outline" onClick={runDue} className="rounded-sm gap-1.5" data-testid="run-due-btn"><Play className="w-3.5 h-3.5" /> Run due</Button>
            <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-recurring-btn"><Plus className="w-3.5 h-3.5" /> New recurring</Button>
          </>
        } />

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Name</th><th>Type</th><th>Cadence</th><th>Next run</th><th className="text-right">Occurrences</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">No recurring definitions</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`recurring-row-${r.id}`}>
                <td className="text-sm font-medium">{r.name}</td>
                <td><span className="text-[10px] font-mono uppercase text-accent">{r.doc_type.replace("_", " ")}</span></td>
                <td className="text-xs"><Repeat className="w-3 h-3 inline mr-1" /> {r.cadence}</td>
                <td className="text-xs"><Calendar className="w-3 h-3 inline mr-1" /> {fmtDate(r.next_run_date)}</td>
                <td className="text-right tabular-nums font-mono text-xs">{r.occurrences}{r.max_occurrences ? ` / ${r.max_occurrences}` : ""}</td>
                <td><Switch checked={r.is_active} onCheckedChange={() => toggle(r.id, r.is_active)} data-testid={`rec-toggle-${r.id}`} /></td>
                <td className="text-right"><button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`rec-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl rounded-none overflow-y-auto space-y-4">
          <SheetHeader><SheetTitle className="font-heading">New Recurring Document</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" placeholder="Monthly retainer" data-testid="rec-name" /></div>
            <div><Label>Doc type</Label><Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}><SelectTrigger className="rounded-sm mt-1" data-testid="rec-doctype"><SelectValue /></SelectTrigger><SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Cadence</Label><Select value={form.cadence} onValueChange={(v) => setForm({ ...form, cadence: v })}><SelectTrigger className="rounded-sm mt-1" data-testid="rec-cadence"><SelectValue /></SelectTrigger><SelectContent>{CADENCES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm mt-1" data-testid="rec-startdate" /></div>
            <div><Label>End date (optional)</Label><Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Branch</Label><Select value={form.body.branch_id} onValueChange={(v) => setForm({ ...form, body: { ...form.body, branch_id: v } })}><SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Branch" /></SelectTrigger><SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>

            {form.doc_type === "sales_invoice" && (
              <div className="col-span-2"><Label>Customer</Label><Select value={form.body.customer_id} onValueChange={(v) => setForm({ ...form, body: { ...form.body, customer_id: v } })}><SelectTrigger className="rounded-sm mt-1" data-testid="rec-customer"><SelectValue placeholder="Customer" /></SelectTrigger><SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            )}
            {form.doc_type === "purchase_bill" && (
              <div className="col-span-2"><Label>Vendor</Label><Select value={form.body.vendor_id} onValueChange={(v) => setForm({ ...form, body: { ...form.body, vendor_id: v } })}><SelectTrigger className="rounded-sm mt-1" data-testid="rec-vendor"><SelectValue placeholder="Vendor" /></SelectTrigger><SelectContent>{vendors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            )}
          </div>

          {form.doc_type !== "journal" ? (
            <div className="border rounded-sm">
              <table className="w-full dense">
                <thead><tr><th>Description</th><th className="text-right w-20">Qty</th><th className="text-right w-24">Rate</th><th className="text-right w-16">GST%</th></tr></thead>
                <tbody>{form.body.lines.map((l, i) => (
                  <tr key={i}>
                    <td className="p-1"><Input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} className="rounded-sm h-8 text-xs" /></td>
                    <td className="p-1"><Input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" /></td>
                    <td className="p-1"><Input type="number" value={l.rate} onChange={(e) => setLine(i, { rate: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" /></td>
                    <td className="p-1"><Input type="number" value={l.gst_rate} onChange={(e) => setLine(i, { gst_rate: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" /></td>
                  </tr>
                ))}</tbody>
              </table>
              <div className="p-2 border-t"><Button variant="ghost" size="sm" onClick={addLine} className="rounded-sm gap-1"><Plus className="w-3.5 h-3.5" /> Line</Button></div>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic">Journal recurring: define lines via API for now (POST /api/recurring with body.lines).</div>
          )}

          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="rec-save">Create recurring</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
